import { PartyService } from 'src/app/services/api/party/party.service';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatPaginator, MatSort, PageEvent, Sort } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { catchError, debounceTime, map, startWith, switchMap, tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';

import { NotificationLevel, NotificationsService } from 'src/app/services/api/notifications/notifications.service';
import { Order, OrdersService } from 'src/app/services/api/orders/orders.service';
import { ProductService } from 'src/app/services/api/product/product.service';
import { BreadcrumbsService } from 'src/app/services/utils/breadcrumbs.service';
import { ORDER_ACTIONS, ORDER_STATES, ORDER_OPERATIONAL_STATES } from 'src/app/utils/Constants';
import { UtilsService } from 'src/app/services/utils/utils.service';

interface Filter {
  _key: string;
  _operation: string;
  _value: string;
}


export class SearchObject {
  private page: number;
  private size: number;
  private sort: any[];

  constructor(private filters: Filter[]) {
    this.filters = filters;
  }

  setPage(page: number) {
    this.page = page;
  }

  setSize(size: number) {
    this.size = size;
  }

  setSort(sortFields: any) {
    this.sort = sortFields;
  }

  getPage() {
    return this.page;
  }

  getSize() {
    return this.size;
  }

  getSort() {
    return this.sort;
  }

  setFilters(filters: Filter[]) {
    this.filters = filters;
  }

  getFilters(): Filter[] {
    return this.filters;
  }
}

interface FormObject {
  fromDate: string;
  toDate: string;
  state: string;
  specification: string;
  action: string;
  crm: string;
  externalId: string;
  isp: string;
}

@Component({
  selector: 'app-orders-body',
  templateUrl: './orders-body.component.html',
  styleUrls: ['./orders-body.component.scss']
})
export class OrdersBodyComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator, null) paginator: MatPaginator;
  @ViewChild(MatSort, null) sort: MatSort;


  displayedColumns: string[] = ['specification', 'action', 'state', 'createdAt', 'lastModifiedAt', 'operationalState', 'externalId', 'isp'];
  data: Order[] = [];
  resultsLength = 0;

  csvLoading = false;
  loading = false;

  searchGroup: FormGroup;
  combos: any;

  paramsObject: any;
  lastSearchObject: SearchObject;

  private searchSubject = new BehaviorSubject<void>(null);

  private subscriptions = new Subscription();

  appData: any;

  pageSize: number;
  pageIndex: number;
  sortActive: string;
  sortDirection: string;

  firstSearch: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private ordersService: OrdersService,
    private breadcrumbsService: BreadcrumbsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private productService: ProductService,
    private partyService: PartyService,
    private appService: AppService,
    private notificationsService: NotificationsService,
    private utilsService: UtilsService
  ) {
  }

  ngOnInit() {

    this.appData = this.appService.settings;

    this.combos = {
      state: [],
      operationalState: [],
      specification: [],
      action: [],
      isp: [],
      origin:[]
    };

    this.combos.operationalState = [{ name: 'Cualquiera', value: null }].concat(ORDER_OPERATIONAL_STATES);
    this.combos.state = [{ name: 'Cualquiera', value: null }].concat(ORDER_STATES);
    this.productService.getProductSpecs().subscribe(
      (data) => {
        this.combos.specification = [{ name: 'Cualquiera', value: null }].concat(data);
      }
    );
    this.combos.action = [{ name: 'Cualquiera', value: null }].concat(ORDER_ACTIONS);
    this.partyService.getCustomers().subscribe(
      (data) => {
        for (const party of data) {
            this.combos.isp.push(party);
        }
        this.combos.isp.sort(function (a, b) {
          if (a.name === "Cualquiera") return -1;
          if (b.name === "Cualquiera") return 1;
          return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : (a.name.toLowerCase() > b.name.toLowerCase()) ? 1 : 0
        });
      }
    );

    this.searchGroup = this.formBuilder.group({
      fromDate: this.formBuilder.control(null),
      toDate: this.formBuilder.control(null),
      state: this.formBuilder.control(null),
      specification: this.formBuilder.control(null),
      action: this.formBuilder.control(null),
      crm: this.formBuilder.control(''),
      operationalState: this.formBuilder.control(null),
      externalId: this.formBuilder.control(''),
      isp: this.formBuilder.control(null),
    });

    this.breadcrumbsService.sendBreadcrumbs(
      {
        breadcrumbs:
          [
            { title: 'Órdenes', url: 'provision/orders/search' }
          ]
      }
    );

    this.pageIndex = 0;
    this.pageSize = 10;
    this.sortActive = 'createdAt';
    this.sortDirection = 'desc';

    this.firstSearch = true;
    this.checkParams();
    this.checkPageAndSort();
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      combineLatest(
        this.searchGroup.valueChanges
          .pipe(
            startWith(this.searchGroup.value),
            debounceTime(500),
            map(
              (formObject: FormObject) => {
                return new SearchObject(
                  Object.entries(formObject).filter(
                    ([key, value]) => !!value).map(([key, value]) => ({ _key: key, _value: value, _operation: null })
                    )
                );
              }
            ),
            tap(() => {
              if (!this.firstSearch) {
                this.paginator.firstPage()
              }
            }),
          ),
        this.sort.sortChange
          .pipe(
            startWith({
              active: 'createdAt',
              direction: 'desc'
            } as Sort),
            tap(() => {
              if (!this.firstSearch) {
                this.paginator.firstPage()
              }
            }),
          ),
        this.paginator.page
          .pipe(
            startWith({
              pageIndex: this.paginator.pageIndex,
              previousPageIndex: 0,
              pageSize: this.paginator.pageSize,
              length: this.paginator.length
            } as PageEvent),
          ),
        this.searchSubject.asObservable(),
      )
        .pipe(
          debounceTime(50),
          switchMap(([search, sort, page]: [SearchObject, Sort, PageEvent, void]) => {
            this.firstSearch = false;
            this.loading = true;
            search.setPage(page.pageIndex);
            search.setSize(page.pageSize);
            if (sort.active && sort.direction) {
              search.setSort([{
                _key: sort.active,
                _value: sort.direction.toUpperCase()
              }]);
            } else {
              search.setSort([]);
            }
            this.updateParamsObject();
            this.updateUrl();
            return this.ordersService
              .getOrders(search).pipe(
                catchError(() => of({
                  elements: [],
                  total: 0,
                })
                ),
              );
          }),
          map(data => {
            this.loading = false;
            this.firstSearch = false;
            this.resultsLength = data.total;
            return data.elements;
          }),
          catchError(() => {
            this.loading = false;
            this.resultsLength = 0;
            return of([]);
          }),
        )
        .subscribe(data => this.data = data));
  }

  private updateParamsObject() {
    this.paramsObject = {};

    this.lastSearchObject = new SearchObject([]);

    if (this.paginator) {
      this.paramsObject['page'] = this.paginator.pageIndex.toString();
      this.lastSearchObject.setPage(this.paginator.pageIndex);
    }

    if (this.paginator && this.paginator.pageSize) {
      this.paramsObject['size'] = this.paginator.pageSize.toString();
      this.lastSearchObject.setSize(this.paginator.pageSize);
    }

    if (this.sort.active) {
      this.paramsObject['sort'] = this.sort.active + ',' + this.sort.direction;
      this.lastSearchObject.setSort([{ _key: this.sort.active, _value: this.sort.direction.toUpperCase() }]);
    }

    if (typeof this.searchGroup.controls['state'].value != 'undefined' && this.searchGroup.controls['state'].value) {
      this.paramsObject['orderState'] = this.searchGroup.controls['state'].value;
      this.lastSearchObject.getFilters().push({ _key: "state", _value: this.searchGroup.controls['state'].value, _operation: null });
    }

    for (const param in this.searchGroup.controls) {
      if (typeof this.searchGroup.controls[param].value != 'undefined' && this.searchGroup.controls[param].value && param != 'state') {
        this.paramsObject[param] = this.searchGroup.controls[param].value;
        this.lastSearchObject.getFilters().push({ _key: param, _value: this.searchGroup.controls[param].value, _operation: null });
      }
    }
  }

  private checkParams() {
    this.activatedRoute.queryParams.subscribe(params => {
      if (this.firstSearch) {
        this.searchGroup.controls['fromDate'].setValue(params['fromDate'] ? new Date(params['fromDate']) : this.searchGroup.controls['fromDate'].value);
        this.searchGroup.controls['toDate'].setValue(params['toDate'] ? new Date(params['toDate']) : this.searchGroup.controls['toDate'].value);
        this.searchGroup.controls['state'].setValue(params['orderState'] ? params['orderState'] : this.searchGroup.controls['state'].value);
        this.searchGroup.controls['specification'].setValue(params['specification'] ? params['specification'] : this.searchGroup.controls['specification'].value);
        this.searchGroup.controls['action'].setValue(params['action'] ? params['action'] : this.searchGroup.controls['action'].value);
        this.searchGroup.controls['crm'].setValue(params['crm'] ? params['crm'] : this.searchGroup.controls['crm'].value);
        this.searchGroup.controls['externalId'].setValue(params['externalId'] ? params['externalId'] : this.searchGroup.controls['externalId'].value);
        this.searchGroup.controls['operationalState'].setValue(params['operationalState'] ? params['operationalState'] : this.searchGroup.controls['operationalState'].value);
        this.searchGroup.controls['isp'].setValue(params['isp'] ? params['isp'] : this.searchGroup.controls['isp'].value);
      }
    });
  }

  private checkPageAndSort() {
    this.activatedRoute.queryParams.subscribe(params => {
      if (this.firstSearch) {
        this.paginator.pageIndex = params['page'] ? params['page'] : this.pageIndex;
        this.pageSize = params['size'] ? params['size'] : this.pageSize;
        this.sortActive = params['sort'] && params['sort'].split(',') ? params['sort'].split(',')[0] : this.sortActive;
        this.sortDirection = params['sort'] && params['sort'].split(',') ? params['sort'].split(',')[1] : this.sortDirection;
      }
    });
  }

  private updateUrl() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: this.paramsObject,
      skipLocationChange: false
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  resetForm() {
    this.searchGroup.reset();
  }

  search() {
    this.searchSubject.next();
  }

  navigateToDetail(row: Order) {
    window.open(`${this.appData.baseUrl}${this.appData.basePath}/provision/orders/detail?id=${row.externalId}`, '_blank');
  }

  get fromDateControl(): FormControl {
    return this.searchGroup.get('fromDate') as FormControl;
  }

  get toDateControl(): FormControl {
    return this.searchGroup.get('toDate') as FormControl;
  }

  get stateControl(): FormControl {
    return this.searchGroup.get('state') as FormControl;
  }

  get specificationControl(): FormControl {
    return this.searchGroup.get('specification') as FormControl;
  }

  get externalIdControl(): FormControl {
    return this.searchGroup.get('externalId') as FormControl;
  }

  get actionControl(): FormControl {
    return this.searchGroup.get('action') as FormControl;
  }

  get crmControl(): FormControl {
    return this.searchGroup.get('crm') as FormControl;
  }

  get operationalStateControl(): FormControl {
    return this.searchGroup.get('operationalState') as FormControl;
  }

  get ispControl(): FormControl {
    return this.searchGroup.get('isp') as FormControl;
  }

  public getOrdersCsv() {
    this.csvLoading = true;
    this.notificationsService.showNotificationWithDuration('El fichero está siendo generado....', NotificationLevel.INFO, 30000);
    const fileDownloadPromise = this.ordersService.getOrdersCsv(this.lastSearchObject);
    this.utilsService.downloadFileV2(fileDownloadPromise, () => this.finish());
  }

  private finish(): void {
		this.csvLoading = false;
	}
}
